import { Head, Link, router } from '@inertiajs/react'
import { Plus, Mail, Send, MessageSquare, FileText } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import { outboundStatus } from '@/lib/outboundStatus'

interface OutboundMessage {
  id: number
  name: string
  message_type: 'chat' | 'post' | 'email'
  delivery_style: 'badge' | 'snippet' | 'full'
  post_format: 'small' | 'large' | null
  reply_type: 'none' | 'reactions' | 'text'
  status: 'draft' | 'active' | 'paused' | 'completed'
  sent_count: number
  opened_count: number
  clicked_count: number
  replied_count: number
  created_at: string
}

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface OutboundMessagesIndexProps {
  outbound_messages: OutboundMessage[]
  currentAgent: Agent
}

export default function OutboundMessagesIndex({ outbound_messages, currentAgent }: OutboundMessagesIndexProps) {
  return (
    <>
      <Head title="Mensagens de Saída" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="outbound" />

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6">
            <h1 className="text-lg font-semibold text-gray-900">Mensagens de Saída</h1>
            <Link
              href="/outbound_messages/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nova Mensagem
            </Link>
          </header>

          <main className="flex-1 overflow-auto p-6">
            <div className="max-w-5xl">
              {outbound_messages.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <Send className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma mensagem de saída ainda</h3>
                  <p className="text-gray-500 mb-4">
                    Crie mensagens de saída para engajar proativamente com seus usuários
                  </p>
                  <Link
                    href="/outbound_messages/new"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    Criar sua primeira mensagem
                  </Link>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Nome
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Enviado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Aberto
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {outbound_messages.map((msg) => (
                        <tr
                          key={msg.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => router.visit(`/outbound_messages/${msg.id}`)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {msg.message_type === 'chat' ? (
                                <MessageSquare className="w-4 h-4 text-gray-400" />
                              ) : msg.message_type === 'email' ? (
                                <Mail className="w-4 h-4 text-gray-400" />
                              ) : (
                                <FileText className="w-4 h-4 text-gray-400" />
                              )}
                              <span className="font-medium text-gray-900">{msg.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {msg.message_type === 'chat' ? 'Chat' :
                             msg.message_type === 'email' ? 'E-mail' :
                             msg.post_format === 'large' ? 'Post (grande)' : 'Post (pequeno)'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${outboundStatus(msg.status).badge}`}
                            >
                              {outboundStatus(msg.status).label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {msg.sent_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {msg.sent_count > 0
                              ? `${Math.round((msg.opened_count / msg.sent_count) * 100)}%`
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  )
}
